import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { ArrowRight, Globe2, GraduationCap, Landmark, Route } from 'lucide-react'
import { useLanguage } from '../../context/LanguageContext'
import { fadeInUp, staggerContainer, viewportOnce } from '../../utils/animations'

const PATHWAYS = [
  {
    key: 'british',
    slug: 'BritishProgram',
    icon: GraduationCap,
    accent: 'bg-primary/10 text-primary ring-primary/20',
    bar: 'bg-primary',
  },
  {
    key: 'american',
    slug: 'AmericanProgram',
    icon: Globe2,
    accent: 'bg-accent/10 text-accent ring-accent/25',
    bar: 'bg-accent',
  },
  {
    key: 'national',
    slug: 'NationalProgram',
    icon: Landmark,
    accent: 'bg-secondary/10 text-secondary ring-secondary/20',
    bar: 'bg-secondary',
  },
]

export default function RoadAheadSection() {
  const { t } = useTranslation()
  const { isRTL } = useLanguage()

  return (
    <section className="my-12 overflow-hidden rounded-3xl border border-border/80 bg-gradient-to-br from-background via-white to-primary/5 shadow-lg shadow-primary/5">
      <div className="border-b border-border/60 px-6 py-8 sm:px-10">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary text-white shadow-md shadow-primary/25">
            <Route className="h-6 w-6" aria-hidden />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-accent">
              {t('roadAhead.label')}
            </p>
            <h3 className="mt-1 text-2xl font-bold text-primary sm:text-3xl">
              {t('roadAhead.title')}
            </h3>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-text/65 sm:text-base">
              {t('roadAhead.subtitle')}
            </p>
          </div>
        </div>
      </div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={staggerContainer}
        className="relative px-6 py-8 sm:px-10 sm:py-10"
      >
        <div
          className="pointer-events-none absolute start-[16%] end-[16%] top-[4.5rem] hidden h-0.5 bg-gradient-to-r from-primary/20 via-accent/40 to-secondary/20 md:block"
          aria-hidden
        />

        <div className="grid gap-6 md:grid-cols-3">
          {PATHWAYS.map(({ key, slug, icon: Icon, accent, bar }, index) => (
            <motion.div key={key} variants={fadeInUp}>
              <Link
                to={`/pages/${slug}`}
                className="group relative flex h-full flex-col rounded-2xl border border-border/70 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/10"
              >
                <div className={`mb-5 h-1 w-12 rounded-full ${bar}`} aria-hidden />

                <div
                  className={`mb-5 flex h-14 w-14 items-center justify-center rounded-2xl ring-1 ${accent}`}
                >
                  <Icon className="h-7 w-7" strokeWidth={1.75} aria-hidden />
                </div>

                <p className="mb-1 text-xs font-bold uppercase tracking-wider text-text/45">
                  {String(index + 1).padStart(2, '0')}
                </p>
                <h4 className="mb-3 text-lg font-bold text-primary group-hover:text-secondary">
                  {t(`roadAhead.pathways.${key}.title`)}
                </h4>
                <p className="mb-6 flex-1 text-sm leading-relaxed text-text/70">
                  {t(`roadAhead.pathways.${key}.text`)}
                </p>

                <span className="inline-flex items-center gap-2 text-sm font-semibold text-accent transition-colors group-hover:text-primary">
                  {t('buttons.learnMoreAbout', { title: t(`roadAhead.pathways.${key}.title`) })}
                  <ArrowRight
                    className={`h-4 w-4 transition-transform group-hover:translate-x-1 ${isRTL ? 'rotate-180 group-hover:-translate-x-1' : ''}`}
                  />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  )
}
